import React from 'react';
import { ClearIndicatorProps, components, ControlProps, DropdownIndicatorProps, GroupBase, MultiValueGenericProps } from 'react-select';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { Icon } from '../Icon/Icon';
import { MATERIAL_ICONS } from '../Icon/icons';
import { useTheme } from 'styled-components';
import { DEFAULT_INPUT_SIZE, inputIconSizes } from '../TextField/TextField';
import { SIZES } from '../../fundamentals/sizes/sizes';
import merge from 'lodash.merge';
import { BadgeCloseIcon, badgeGaps, badgeIconSizes, badgePaddingSizes, badgeTextSizes } from '../Badge/Badge';
import { FONT_WEIGHTS } from '../../fundamentals/typography/typography';
import { SELECT_SPACE } from './selectStyleOverrides';

const DEFAULT_SEARCH_ICON = MATERIAL_ICONS.Search;
const DEFAULT_EXPAND_ICON = MATERIAL_ICONS.ExpandMore;

const useSelectIcon = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: DropdownIndicatorProps<Option, IsMulti, Group> | ClearIndicatorProps<Option, IsMulti, Group> | ControlProps<Option, IsMulti, Group>
) => {
  const { isDisabled, selectSize = DEFAULT_INPUT_SIZE } = props.selectProps;
  const { textFieldColours } = useTheme();
  const colour = textFieldColours[isDisabled ? `disabled` : `placeholder`].text;

  return {
    size: inputIconSizes[selectSize],
    colour
  };
};

export const DropdownIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: DropdownIndicatorProps<Option, IsMulti, Group>) => {
  const { generateTestId } = useTestId(props, 'DropdownIndicator');
  const iconProps = useSelectIcon(props);
  const { isSearchable } = props.selectProps;

  return (
    <components.DropdownIndicator className={isSearchable ? `` : `rotate-on-open`} {...props}>
      <Icon {...iconProps} name={isSearchable ? DEFAULT_SEARCH_ICON : DEFAULT_EXPAND_ICON} {...generateTestId()} />
    </components.DropdownIndicator>
  );
};

export const ClearIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: ClearIndicatorProps<Option, IsMulti, Group>) => {
  const { generateTestId } = useTestId(props, 'ClearIndicator');
  const iconProps = useSelectIcon(props);

  return (
    <components.ClearIndicator {...props}>
      <Icon {...iconProps} name={MATERIAL_ICONS.Clear} {...generateTestId()} />
    </components.ClearIndicator>
  );
};

export const Control = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: ControlProps<Option, IsMulti, Group>) => {
  const { generateTestId } = useTestId(props, 'Control');
  const iconProps = useSelectIcon(props);
  // @ts-ignore
  const { iconName } = props.selectProps;

  return (
    <components.Control {...props}>
      {iconName && <Icon gutterLeft={8} {...iconProps} name={iconName} {...generateTestId()} />}
      {props.children}
    </components.Control>
  );
};

const BADGE_SIZE = SIZES.XX_SMALL;

export const MultiValueContainer = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: MultiValueGenericProps<Option, IsMulti, Group>
) => {
  const theme = useTheme();

  const customInnerCss = {
    innerProps: {
      css: {
        backgroundColor: theme.colours.status.neutral.light!.colour,
        borderRadius: theme.borderRadius[BADGE_SIZE],
        padding: badgePaddingSizes[BADGE_SIZE],
        columnGap: badgeGaps[BADGE_SIZE],
        marginTop: theme.spaces[SELECT_SPACE],
        marginBottom: 0
      }
    }
  };

  const customProps = merge(props, customInnerCss);
  return <components.MultiValueContainer {...customProps} />;
};

export const MultiValueLabel = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: MultiValueGenericProps<Option, IsMulti, Group>) => {
  const theme = useTheme();

  const customInnerCss = {
    innerProps: {
      css: {
        fontSize: theme.fonts.fontSizes[badgeTextSizes[BADGE_SIZE]],
        lineHeight: theme.fonts.lineHeightSizes[badgeTextSizes[BADGE_SIZE]],
        fontWeight: theme.fonts.fontWeights[FONT_WEIGHTS.MEDIUM],
        padding: 0,
        paddingLeft: 0
      }
    }
  };

  const customProps = merge(props, customInnerCss);
  return <components.MultiValueLabel {...customProps} />;
};

export const MultiValueRemove = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>(props: MultiValueGenericProps<Option, IsMulti, Group>) => {
  const theme = useTheme();

  const customInnerCss = {
    innerProps: {
      css: {
        ':hover': {
          backgroundColor: 'none',
          color: 'inherit'
        },
        paddingRight: 0
      }
    }
  };

  const customProps = merge(props, customInnerCss);
  return (
    <components.MultiValueRemove {...customProps}>
      <BadgeCloseIcon colour={theme.colours.status.neutral.light!.text} name={MATERIAL_ICONS.Cancel} size={badgeIconSizes[BADGE_SIZE]} />
    </components.MultiValueRemove>
  );
};
